import { metricColumn } from './dashboardMetricColumn';
import { Link } from '@tanstack/react-router';
import {
  buildDashboardDataTableColumns,
  DashboardDataTableColumnKind,
  type DashboardDataTableColumnDefinition,
} from '@shigo-ui/dashboard-ui';
import { dashboardT } from '../../../../../lib/messages';
import { dashboardSrpm } from '../../../format';
import { formatCurrency, formatNumber, formatRatio, percentValue } from '../../../../../lib/format';
import {
  DashboardPublisherColumn,
  DashboardNetworkColumn,
  type DashboardDemandIntegrationRow,
  type DashboardNetworkRow,
  type DashboardPublisherRow,
} from '../../../model';
import { dashboardTruncatedNameClassName, DashboardTruncatedName } from '../DashboardTruncatedName';

/** A 0..1 ratio as a one-decimal percentage. */
function formatPercentValue(value: number | null | undefined) {
  const percent = percentValue(value);
  return `${formatNumber(percent, 1)}${percent === null || percent === undefined ? '' : '%'}`;
}

export function publisherColumns(selected: DashboardPublisherColumn[], showComparison: boolean) {
  const definitions: Record<
    DashboardPublisherColumn,
    DashboardDataTableColumnDefinition<DashboardPublisherRow>
  > = {
    [DashboardPublisherColumn.Publisher]: {
      id: 'name',
      header: dashboardT('dashboard.table.publisher'),
      kind: DashboardDataTableColumnKind.Custom,
      className: 'w-[220px]',
      headerClassName: 'w-[220px]',
      cell: (row) => (
        <DashboardTruncatedName name={row.publisherName}>
          <Link
            className={`${dashboardTruncatedNameClassName} text-primary font-medium hover:underline`}
            to="/supply/publishers/$publisherId/details/overview"
            params={{ publisherId: row.publisherId }}
          >
            {row.publisherName}
          </Link>
        </DashboardTruncatedName>
      ),
    },
    [DashboardPublisherColumn.AdRequests]: metricColumn(
      'adRequests',
      'dashboard.metric.adRequests',
      (row) => row.adRequests,
      (row) => row.adRequestsChange,
      showComparison,
      formatNumber,
    ),
    [DashboardPublisherColumn.AdImpressions]: metricColumn(
      'adImpressions',
      'dashboard.metric.adImpressions',
      (row) => row.adImpressions,
      (row) => row.adImpressionsChange,
      showComparison,
      formatNumber,
    ),
    [DashboardPublisherColumn.FillRate]: metricColumn(
      'fillRate',
      'dashboard.metric.fillRate',
      (row) => row.adRequestsFilled,
      (row) => row.adRequestsFilledChange,
      showComparison,
      formatRatio,
    ),
    [DashboardPublisherColumn.Rpm]: metricColumn(
      'rpm',
      'dashboard.metric.rpm',
      (row) => row.rpm,
      (row) => row.rpmChange,
      showComparison,
      formatCurrency,
    ),
    [DashboardPublisherColumn.Srpm]: metricColumn(
      'srpm',
      'dashboard.metric.srpm',
      (row) => row.srpm,
      (row) => row.srpmChange,
      showComparison,
      formatCurrency,
    ),
    [DashboardPublisherColumn.RevenuePercentage]: metricColumn(
      'revenuePct',
      'dashboard.table.revenuePct',
      (row) => row.revenuePct,
      (row) => row.revenuePctChange,
      showComparison,
      formatPercentValue,
    ),
    [DashboardPublisherColumn.MarginPercentage]: metricColumn(
      'marginPct',
      'dashboard.table.marginPct',
      (row) => row.networkMarginPct,
      (row) => row.networkMarginPctChange,
      showComparison,
      formatPercentValue,
    ),
    [DashboardPublisherColumn.Revenue]: metricColumn(
      'revenue',
      'dashboard.metric.revenue',
      (row) => row.revenue,
      (row) => row.revenueChange,
      showComparison,
      formatCurrency,
    ),
    [DashboardPublisherColumn.PublisherRevenue]: metricColumn(
      'publisherRevenue',
      'dashboard.metric.publisherRevenue',
      (row) => row.pubRevenue,
      (row) => row.pubRevenueChange,
      showComparison,
      formatCurrency,
    ),
    [DashboardPublisherColumn.Profit]: metricColumn(
      'profit',
      'dashboard.metric.profit',
      (row) => row.networkProfit,
      (row) => row.networkProfitChange,
      showComparison,
      formatCurrency,
    ),
  };
  return buildDashboardDataTableColumns(selected.map((column) => definitions[column]));
}

export function networkColumns(selected: DashboardNetworkColumn[], showComparison: boolean) {
  const definitions: Record<
    DashboardNetworkColumn,
    DashboardDataTableColumnDefinition<DashboardNetworkRow>
  > = {
    [DashboardNetworkColumn.Network]: {
      id: 'name',
      header: dashboardT('dashboard.table.network'),
      kind: DashboardDataTableColumnKind.Text,
      value: (row) => row.networkName,
      className: 'w-[220px]',
    },
    [DashboardNetworkColumn.AdRequests]: metricColumn(
      'adRequests',
      'dashboard.metric.adRequests',
      (row) => row.adRequests,
      (row) => row.adRequestsChange,
      showComparison,
      formatNumber,
    ),
    [DashboardNetworkColumn.AdImpressions]: metricColumn(
      'adImpressions',
      'dashboard.metric.adImpressions',
      (row) => row.adImpressions,
      (row) => row.adImpressionsChange,
      showComparison,
      formatNumber,
    ),
    [DashboardNetworkColumn.Revenue]: metricColumn(
      'revenue',
      'dashboard.metric.revenue',
      (row) => row.revenue,
      (row) => row.revenueChange,
      showComparison,
      formatCurrency,
    ),
    [DashboardNetworkColumn.NetworkProfit]: metricColumn(
      'networkProfit',
      'dashboard.table.networkProfit',
      (row) => row.networkProfit,
      (row) => row.networkProfitChange,
      showComparison,
      formatCurrency,
    ),
  };
  return buildDashboardDataTableColumns(selected.map((column) => definitions[column]));
}

export function integrationColumns(showComparison: boolean) {
  return buildDashboardDataTableColumns<DashboardDemandIntegrationRow>([
    {
      id: 'name',
      header: dashboardT('dashboard.table.integration'),
      kind: DashboardDataTableColumnKind.Custom,
      className: 'w-[220px]',
      cell: (row) => (
        <DashboardTruncatedName name={row.demandIntegrationName}>
          {row.demandIntegrationId && row.demandPartnerId ? (
            <Link
              className={`${dashboardTruncatedNameClassName} text-primary font-medium hover:underline`}
              to="/demand/integrations/$integrationId/demand/$demandPartnerId/details/overview"
              params={{
                integrationId: row.demandIntegrationId,
                demandPartnerId: row.demandPartnerId,
              }}
            >
              {row.demandIntegrationName}
            </Link>
          ) : (
            <span className={dashboardTruncatedNameClassName}>{row.demandIntegrationName}</span>
          )}
        </DashboardTruncatedName>
      ),
    },
    metricColumn(
      'bidRequests',
      'dashboard.table.bidRequests',
      (row) => row.bidRequests,
      (row) => row.bidRequestsChange,
      showComparison,
      formatNumber,
    ),
    metricColumn(
      'bidResponses',
      'dashboard.table.bidResponses',
      (row) => row.bidResponses,
      (row) => row.bidResponsesChange,
      showComparison,
      formatNumber,
    ),
    metricColumn(
      'bidRatio',
      'dashboard.table.bidRatio',
      (row) => row.bidRatio,
      (row) => row.bidRatioChange,
      showComparison,
      formatRatio,
    ),
    metricColumn(
      'winRatio',
      'dashboard.table.winRatio',
      (row) => row.winRatio,
      (row) => row.winRatioChange,
      showComparison,
      formatRatio,
    ),
    metricColumn(
      'fillRate',
      'dashboard.metric.fillRate',
      (row) => row.adRequestsFilled,
      (row) => row.adRequestsFilledChange,
      showComparison,
      formatRatio,
    ),
    metricColumn(
      'revenue',
      'dashboard.metric.revenue',
      (row) => row.revenue,
      (row) => row.revenueChange,
      showComparison,
      formatCurrency,
    ),
    metricColumn(
      'srpm',
      'dashboard.metric.srpm',
      (row) => row.srpm ?? dashboardSrpm(row.revenue, row.bidRequests),
      (row) => row.srpmChange,
      showComparison,
      formatCurrency,
    ),
  ]);
}

export { partnerColumns } from './dashboardPartnerColumns';
